// src/pages/Perfil.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useReclamos } from "../hooks/useReclamos";
import { ESTADOS_CONFIG, ROLES_CONFIG, ROUTES } from "../constants";
import { formatRelativeTime } from "../components/common/formatHelpers";
import { Card } from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Modal } from "../components/common/Modal";
import { Alert } from "../components/common/Alert";
import { Loading } from "../components/common/Loading";
import apiService from "../services/api.service";
import { auth } from "../firebase/config";
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber
} from "firebase/auth";
import OtpInput from 'react-otp-input';

export default function Perfil() {
  const navigate = useNavigate();
  const { userData, loading: authLoading, reloadUserData, hasAdminAccess, logout } = useAuth();
  const { reclamos, loading: reclamosLoading } = useReclamos(false);

  // Estados para modales
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Estados para formularios
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  // Estados de UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Estados para editar perfil
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [formData, setFormData] = useState({
    nombre: userData?.nombre || ""
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Estados para actualizar teléfono
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [phoneVerificationId, setPhoneVerificationId] = useState(null);
  const [phoneRecaptchaVerifier, setPhoneRecaptchaVerifier] = useState(null);
  const [phoneResendCountdown, setPhoneResendCountdown] = useState(0);
  const [isPhoneSubmitting, setIsPhoneSubmitting] = useState(false);
  const [phoneStep, setPhoneStep] = useState("input"); // 'input', 'verify', 'success'
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  useEffect(() => {
    if (hasAdminAccess && !authLoading) {
      loadHistorial();
    }
  }, [hasAdminAccess, authLoading]);

  const loadHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const data = await apiService.getMyHistorial();
      setHistorial(data);
    } catch (error) {
      console.error("Error cargando historial:", error);
    } finally {
      setLoadingHistorial(false);
    }
  };

  // Efecto para inicializar recaptcha cuando se abre el modal
  useEffect(() => {
    if (showPhoneModal && !phoneRecaptchaVerifier) {
      const recaptcha = new RecaptchaVerifier(
        auth,
        "recaptcha-container-phone",
        {
          size: "invisible"
        }
      );

      setPhoneRecaptchaVerifier(recaptcha);

      return () => {
        if (recaptcha) {
          recaptcha.clear();
        }
      };
    }
  }, [showPhoneModal, phoneRecaptchaVerifier]);

  // Efecto para el countdown de reenvío
  useEffect(() => {
    let timer;
    if (phoneResendCountdown > 0) {
      timer = setTimeout(() => {
        setPhoneResendCountdown(phoneResendCountdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [phoneResendCountdown]);

  const validarCaracteresNombre = (nombre) => {
    const regex = /^[a-zA-ZÀ-ÿñÑ]+(?:[ '-][a-zA-ZÀ-ÿñÑ]+)*$/;

    // Verificar que no contenga números
    if (/\d/.test(nombre)) {
      return false;
    }

    // Verificar que no contenga símbolos especiales no permitidos
    if (/[!@#$%^&*()_+=[\]{};":\\|,<>/?~`]/.test(nombre)) {
      return false;
    }

    return regex.test(nombre.trim());
  };

  // Función para manejar cambios en el formulario de perfil
  const handleProfileChange = (field, value) => {
    if (field === 'nombre') {
      // Filtrar caracteres no permitidos en tiempo real
      const filteredValue = value.replace(/[^a-zA-ZÀ-ÿñÑ\s'.-]/g, '');
      setFormData(prev => ({ ...prev, [field]: filteredValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Limpiar error de validación específico
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Función para validar el formulario de perfil
  const validateProfileForm = () => {
    const errors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = "El nombre es requerido";
    } else if (formData.nombre.trim().length < 2) {
      errors.nombre = "El nombre debe tener al menos 2 caracteres";
    } else if (formData.nombre.trim().length > 50) {
      errors.nombre = "El nombre no puede tener más de 50 caracteres";
    } else if (!validarCaracteresNombre(formData.nombre)) {
      errors.nombre = "El nombre contiene caracteres inválidos. Solo se permiten letras, espacios, acentos y los caracteres: ' . -";
    }

    if (formData.telefono && !/^\d{8,15}$/.test(formData.telefono.replace(/\s/g, ''))) {
      errors.telefono = "Teléfono inválido (8-15 dígitos)";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Función para cambiar email
  const handleEmailChange = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await apiService.updateCredentials({
        nuevo_email: newEmail,
        password_actual: currentPassword
      });

      // Mostrar mensaje de éxito
      setSuccessMessage("Email actualizado. Redirigiendo al login...");
      setShowEmailModal(false);

      // Cerrar sesión después de 2 segundos
      setTimeout(async () => {
        await logout(ROUTES.LOGIN);
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message || "Error al actualizar el email");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para cambiar contraseña
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setErrorMessage("Las contraseñas no coinciden");
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await apiService.updateCredentials({
        nueva_password: newPassword,
        password_actual: currentPassword
      });

      // Mostrar mensaje de éxito
      setSuccessMessage("Contraseña actualizada. Redirigiendo al login...");
      setShowPasswordModal(false);

      // Cerrar sesión después de 2 segundos
      setTimeout(async () => {
        await logout(ROUTES.LOGIN);
      }, 2000);
    } catch (err) {
      setErrorMessage(err.message || "Error al actualizar la contraseña");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!validateProfileForm()) return;
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await apiService.updateProfile(formData);

      setSuccessMessage("Perfil actualizado correctamente");
      setShowProfileModal(false);

      if (reloadUserData) await reloadUserData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || "Error al actualizar perfil");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Funciones para actualizar teléfono
  const requestPhoneOtp = async (e) => {
    if (e) e.preventDefault();

    setErrorMessage("");
    setIsPhoneSubmitting(true);

    try {
      if (!phoneRecaptchaVerifier) {
        throw new Error("El reCAPTCHA no está listo. Por favor, recargue la página.");
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error("No hay usuario autenticado");
      }

      // Formatear número
      let formattedPhone = phoneNumber;
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+549${formattedPhone}`;
      }

      // Usar PhoneAuthProvider para verificación
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(
        formattedPhone,
        phoneRecaptchaVerifier
      );

      // Guardar el verificationId para usarlo después
      setPhoneVerificationId(verificationId);
      setPhoneStep("verify");
      setSuccessMessage("Código enviado. Revisa tu teléfono.");
      setPhoneResendCountdown(60);

    } catch (err) {
      console.error("Error al enviar OTP:", err);
      setPhoneResendCountdown(0);

      if (err.code === "auth/too-many-requests") {
        setErrorMessage("Demasiados intentos. Intenta más tarde.");
      } else if (err.code === "auth/invalid-phone-number") {
        setErrorMessage("Número inválido. Verifícalo.");
      } else {
        setErrorMessage(err.message || "Error al enviar código. Intenta nuevamente.");
      }
    } finally {
      setIsPhoneSubmitting(false);
    }
  };

  const verifyPhoneOtp = async () => {
    setErrorMessage("");
    setIsPhoneSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No hay usuario autenticado");
      }

      if (!phoneVerificationId) {
        throw new Error("No hay una verificación en curso.");
      }

      // Crear la credencial con el código OTP
      const credential = PhoneAuthProvider.credential(
        phoneVerificationId,
        phoneOtp
      );

      // ACTUALIZAR el número de teléfono del usuario actual
      await updatePhoneNumber(user, credential);

      // También actualizar en tu base de datos
      await apiService.updateProfile({
        telefono: phoneNumber.replace(/\s/g, '')
      });

      setPhoneStep("success");
      setSuccessMessage("Teléfono actualizado correctamente");

      // Recargar datos del usuario
      if (reloadUserData) {
        await reloadUserData();
      }

      setTimeout(() => {
        setShowPhoneModal(false);
        resetPhoneModal();
      }, 2000);

    } catch (err) {
      console.error("Error al verificar OTP:", err);

      if (err.code === "auth/invalid-verification-code") {
        setErrorMessage("Código inválido. Intenta nuevamente.");
      } else if (err.code === "auth/requires-recent-login") {
        setErrorMessage("Debes iniciar sesión nuevamente para cambiar tu número.");
      } else {
        setErrorMessage(err.message || "Error al verificar el código.");
      }
    } finally {
      setIsPhoneSubmitting(false);
    }
  };

  const resetPhoneModal = () => {
    setPhoneStep("input");
    setPhoneNumber("");
    setPhoneOtp("");
    setPhoneVerificationId(null);
    setPhoneResendCountdown(0);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const formatFecha = (fechaISO) => {
    if (!fechaISO) return "No disponible";
    try {
      return new Date(fechaISO).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "Fecha inválida";
    }
  };

  const tiempoRelativo = (fechaISO) => {
    if (!fechaISO) return "No disponible";
    try {
      const ahora = new Date();
      const fecha = new Date(fechaISO);
      const diffMs = ahora - fecha;
      const diffMin = Math.floor(diffMs / 60000);

      if (diffMin < 1) return "Hace unos segundos";
      if (diffMin < 60) return `Hace ${diffMin} minuto${diffMin > 1 ? 's' : ''}`;

      const diffHrs = Math.floor(diffMin / 60);
      if (diffHrs < 24) return `Hace ${diffHrs} hora${diffHrs > 1 ? 's' : ''}`;

      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 30) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;

      const diffMonths = Math.floor(diffDays / 30);
      return `Hace ${diffMonths} mes${diffMonths > 1 ? 'es' : ''}`;
    } catch {
      return "No disponible";
    }
  };

  const getRoleLabel = (role) => {
    const roleConfig = ROLES_CONFIG.find(r => r.value === role);
    return roleConfig?.label || role;
  };

  const getRoleBadgeVariant = (role) => {
    if (role === "admin") return "danger";
    if (role === "agente_municipal") return "info";
    return "default";
  };


  if (authLoading || reclamosLoading) {
    return <Loading text="Cargando perfil..." />;
  }

  if (!userData) {
    return (
      <Card padding="60px 40px">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "64px", marginBottom: "16px" }}>⚠️</div>
          <h2 style={{ fontSize: "24px", fontWeight: "600", color: "#1e293b" }}>
            Error al cargar perfil
          </h2>
          <p style={{ fontSize: "16px", color: "#64748b" }}>
            No se pudo obtener la información del usuario
          </p>
        </div>
      </Card>
    );
  }

  // Calcular estadísticas
  const estadisticas = ESTADOS_CONFIG.map(estado => ({
    ...estado,
    count: reclamos.filter(r => r.estado === estado.value).length
  }));

  return (
    <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
      {/* Mensajes */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}

      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{
          fontSize: "32px",
          fontWeight: "700",
          color: "#1e293b",
          margin: "0 0 8px 0"
        }}>
          Mi Perfil
        </h1>
        <p style={{
          fontSize: "16px",
          color: "#64748b",
          margin: 0
        }}>
          Información personal y estadísticas de actividad
        </p>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "24px",
        marginBottom: "24px"
      }}>
        {/* Información Personal */}
        <Card padding="32px">
          <div style={{
            textAlign: "center",
            marginBottom: "24px"
          }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "#00a680",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "36px",
              margin: "0 auto 16px",
              boxShadow: "0 4px 12px rgba(0, 166, 128, 0.3)"
            }}>
              👤
            </div>
            <h2 style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1e293b",
              margin: "0 0 8px 0",
              wordBreak: "break-word"
            }}>
              {userData.nombre || userData.email}
            </h2>
            <Badge variant={getRoleBadgeVariant(userData.role)}>
              {getRoleLabel(userData.role)}
            </Badge>
          </div>

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingTop: "24px",
            borderTop: "1px solid #e2e8f0"
          }}>
            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Email</span>
                <button
                  onClick={() => setShowEmailModal(true)}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#00a680",
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f0fdf4";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  ✏️ Cambiar
                </button>
              </div>
              <div style={{
                fontSize: "15px",
                color: "#1e293b",
                wordBreak: "break-word"
              }}>
                {userData.email}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Contraseña</span>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#00a680",
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f0fdf4";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  🔒 Cambiar
                </button>
              </div>
              <div style={{
                fontSize: "15px",
                color: "#64748b"
              }}>
                ••••••••
              </div>
            </div>
            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <span>Nombre</span>
                <button
                  onClick={() => {
                    setFormData({ nombre: userData?.nombre || "" });
                    setShowProfileModal(true);
                  }}
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#00a680",
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f0fdf4";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                  }}
                >
                  ✏️ {userData?.nombre ? "Cambiar" : "Establecer"}
                </button>
              </div>
              <div style={{
                fontSize: "15px",
                color: userData?.nombre ? "#1e293b" : "#94a3b8",
                fontStyle: userData?.nombre ? "normal" : "italic"
              }}>
                {userData?.nombre || "No establecido"}
              </div>
            </div>
            {userData.telefono && (
              <div>
                <div style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#64748b",
                  marginBottom: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <span>Teléfono</span>
                  <button
                    onClick={() => setShowPhoneModal(true)}
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#00a680",
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f0fdf4";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    📞 Cambiar
                  </button>
                </div>
                <div style={{
                  fontSize: "15px",
                  color: "#1e293b"
                }}>
                  {userData.telefono}
                </div>
              </div>
            )}
            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "4px"
              }}>
                Fecha de registro
              </div>
              <div style={{
                fontSize: "15px",
                color: "#1e293b"
              }}>
                {formatFecha(userData.fecha_registro)}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "4px"
              }}>
                Último acceso
              </div>
              <div style={{
                fontSize: "15px",
                color: "#1e293b"
              }}>
                {tiempoRelativo(userData.ultimo_login)}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#64748b",
                marginBottom: "4px"
              }}>
                Estado de cuenta
              </div>
              <Badge variant={userData.activo ? "success" : "danger"}>
                {userData.activo ? "✓ Activa" : "✗ Inactiva"}
              </Badge>
            </div>
          </div>
        </Card>

        {/* Estadísticas de Reclamos */}

        {!hasAdminAccess && (
          <Card padding="32px">
            <h3 style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
              margin: "0 0 24px 0"
            }}>
              📊 Mis Estadísticas
            </h3>

            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px"
            }}>
              {/* Total */}
              <div style={{
                padding: "20px",
                backgroundColor: "#f8fafc",
                borderRadius: "12px",
                border: "2px solid #e2e8f0"
              }}>
                <div style={{
                  fontSize: "32px",
                  fontWeight: "700",
                  color: "#00a680",
                  marginBottom: "4px"
                }}>
                  {reclamos.length}
                </div>
                <div style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b"
                }}>
                  Total de reclamos
                </div>
              </div>

              {/* Por estado */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px"
              }}>
                {estadisticas.map(est => (
                  <div
                    key={est.value}
                    style={{
                      padding: "16px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "8px",
                      textAlign: "center"
                    }}
                  >
                    <div style={{
                      fontSize: "24px",
                      fontWeight: "700",
                      color: est.color,
                      marginBottom: "4px"
                    }}>
                      {est.count}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500"
                    }}>
                      {est.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Historial de Cambios Recientes (Solo para Admin y Agente Municipal) */}
      {hasAdminAccess && (
        <Card padding="32px">
          <h3 style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#1e293b",
            margin: "0 0 20px 0"
          }}>
            📝 Historial de Cambios Recientes
          </h3>

          {loadingHistorial ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loading text="Cargando historial..." />
            </div>
          ) : historial.length > 0 ? (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px"
            }}>
              {historial.slice(0, 5).map((cambio) => (
                <div
                  key={cambio.id}
                  style={{
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "16px"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#1e293b",
                      marginBottom: "4px"
                    }}>
                      {cambio.comentario || `Cambio en reclamo #${cambio.reclamo_id}`} del Reclamo: #{cambio.reclamo_id}
                    </div>

                    <div style={{
                      fontSize: "12px",
                      color: "#94a3b8"
                    }}>
                      {formatRelativeTime(cambio.fecha)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#64748b"
            }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>📋</div>
              <p style={{ margin: 0, fontSize: "16px" }}>
                Aún no has realizado cambios en el sistema
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Actividad Reciente */}
      {reclamos.length > 0 && (
        <Card padding="32px">
          <h3 style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#1e293b",
            margin: "0 0 20px 0"
          }}>
            📋 Actividad Reciente
          </h3>

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px"
          }}>
            {reclamos.slice(0, 5).map((reclamo) => {
              const estadoConfig = ESTADOS_CONFIG.find(e => e.value === reclamo.estado);
              return (
                <div
                  key={reclamo.id}
                  style={{
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#1e293b",
                      marginBottom: "4px"
                    }}>
                      {reclamo.titulo}
                    </div>
                    <div style={{
                      fontSize: "13px",
                      color: "#64748b"
                    }}>
                      {reclamo.categoria} • {formatFecha(reclamo.fecha_creacion)}
                    </div>
                  </div>
                  <Badge variant={
                    estadoConfig?.value === "resuelto" ? "success" :
                      estadoConfig?.value === "en_progreso" ? "info" : "warning"
                  }>
                    {estadoConfig?.label || reclamo.estado}
                  </Badge>
                </div>
              );
            })}
          </div>

          {reclamos.length > 5 && (
            <div style={{
              marginTop: "16px",
              textAlign: "center"
            }}>
              <button
                onClick={() => navigate(ROUTES.MIS_RECLAMOS)}
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#00a680",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Ver todos los reclamos →
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Modal Editar Nombre */}
      <Modal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          setErrorMessage("");
          setValidationErrors({});
          // Resetear form data a los valores actuales del usuario
          setFormData({
            nombre: userData?.nombre || ""
          });
        }}
        title="✏️ Cambiar Nombre"
      >
        <form onSubmit={handleProfileUpdate}>
          <Input
            type="text"
            label="Nombre"
            placeholder="Tu nombre completo"
            icon="👤"
            value={formData.nombre}
            onChange={(e) => handleProfileChange('nombre', e.target.value)}
            error={validationErrors.nombre}
            required
            disabled={isSubmitting}
          />

          <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? "Guardando..." : "Actualizar nombre"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowProfileModal(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Cambiar Email */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setNewEmail("");
          setCurrentPassword("");
          setErrorMessage("");
        }}
        title="📧 Cambiar Email"
      >
        <form onSubmit={handleEmailChange}>
          <Input
            type="email"
            label="Nuevo Email"
            placeholder="nuevo@email.com"
            icon="📧"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />

          <Input
            type="password"
            label="Contraseña Actual"
            placeholder="Tu contraseña actual para confirmar"
            icon="🔒"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />

          <div style={{
            padding: "12px 16px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#92400e",
            marginBottom: "24px",
            display: "flex",
            gap: "8px"
          }}>
            <span>⚠️</span>
            <span>Deberás iniciar sesión nuevamente con tu nuevo email</span>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? "Actualizando..." : "Actualizar Email"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowEmailModal(false);
                setNewEmail("");
                setCurrentPassword("");
                setErrorMessage("");
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Cambiar Contraseña */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setNewPassword("");
          setConfirmPassword("");
          setCurrentPassword("");
          setErrorMessage("");
        }}
        title="🔒 Cambiar Contraseña"
      >
        <form onSubmit={handlePasswordChange}>
          <Input
            type="password"
            label="Nueva Contraseña"
            placeholder="••••••••"
            icon="🔒"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />

          <Input
            type="password"
            label="Contraseña Actual"
            placeholder="Tu contraseña actual para confirmar"
            icon="🔒"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />

          <Input
            type="password"
            label="Confirmar Nueva Contraseña"
            placeholder="••••••••"
            icon="🔒"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />

          <div style={{
            padding: "12px 16px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            fontSize: "14px",
            color: "#92400e",
            marginBottom: "24px",
            display: "flex",
            gap: "8px"
          }}>
            <span>⚠️</span>
            <span>Deberás iniciar sesión nuevamente con tu nueva contraseña</span>
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? "Actualizando..." : "Actualizar Contraseña"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowPasswordModal(false);
                setNewPassword("");
                setConfirmPassword("");
                setCurrentPassword("");
                setErrorMessage("");
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Cambiar Teléfono */}
      <Modal
        isOpen={showPhoneModal}
        onClose={() => {
          setShowPhoneModal(false);
          resetPhoneModal();
        }}
        title="📞 Cambiar Teléfono"
      >
        <div style={{ padding: "16px 0" }}>
          {phoneStep === "input" && (
            <form onSubmit={requestPhoneOtp}>
              <Input
                type="tel"
                label="Nuevo Número de Teléfono"
                placeholder="Ej: 3512345678"
                icon="📞"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                required
                disabled={isPhoneSubmitting}
                helptext="Ingresa tu número sin el 0 y sin el 15. Ej: 3512345678"
              />

              <div style={{
                padding: "12px 16px",
                backgroundColor: "#f0fdf4",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#166534",
                marginBottom: "16px",
                display: "flex",
                gap: "8px"
              }}>
                <span>ℹ️</span>
                <span>Recibirás un código SMS para verificar tu número</span>
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  disabled={!phoneNumber || phoneNumber.length < 8 || isPhoneSubmitting}
                >
                  {isPhoneSubmitting ? "Enviando código..." : "Enviar código SMS"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowPhoneModal(false);
                    resetPhoneModal();
                  }}
                  disabled={isPhoneSubmitting}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          )}

          {phoneStep === "verify" && (
            <div>
              <p style={{
                marginBottom: "16px",
                color: "#64748b",
                fontSize: "14px"
              }}>
                Ingresa el código de 6 dígitos enviado a <strong>+54 {phoneNumber}</strong>
              </p>

              <div style={{ marginBottom: "24px" }}>
                <div style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#64748b",
                  marginBottom: "8px"
                }}>
                  Código de verificación
                </div>

                <OtpInput
                  value={phoneOtp}
                  onChange={setPhoneOtp}
                  numInputs={6}
                  renderInput={(props) => (
                    <input
                      {...props}
                      style={{
                        width: '40px',
                        height: '40px',
                        margin: '0 4px',
                        fontSize: '16px',
                        borderRadius: '8px',
                        border: '2px solid #e2e8f0',
                        textAlign: 'center',
                        outline: 'none',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#00a680';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e2e8f0';
                      }}
                    />
                  )}
                />
              </div>

              <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
                <Button
                  onClick={verifyPhoneOtp}
                  variant="primary"
                  fullWidth
                  disabled={phoneOtp.length !== 6 || isPhoneSubmitting}
                >
                  {isPhoneSubmitting ? "Verificando..." : "Verificar y actualizar"}
                </Button>
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <Button
                  onClick={requestPhoneOtp}
                  variant="secondary"
                  disabled={phoneResendCountdown > 0 || isPhoneSubmitting}
                  fullWidth
                >
                  {phoneResendCountdown > 0
                    ? `Reenviar en ${phoneResendCountdown}s`
                    : 'Reenviar código'
                  }
                </Button>

                <Button
                  onClick={() => {
                    setShowPhoneModal(false);
                    resetPhoneModal();
                  }}
                  variant="secondary"
                  disabled={isPhoneSubmitting}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {phoneStep === "success" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
              <h3 style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#00a680",
                marginBottom: "8px"
              }}>
                ¡Teléfono actualizado!
              </h3>
              <p style={{ color: "#64748b" }}>
                Tu número de teléfono ha sido actualizado correctamente en el sistema.
              </p>
            </div>
          )}

          {/* Contenedor invisible para reCAPTCHA */}
          <div id="recaptcha-container-phone" style={{ display: "none" }} />
        </div>
      </Modal>
    </div>
  );
}